import { isArray, keyBy, max, sumBy } from 'lodash';
import {
	colorToPercent,
	getStakeOrConstraintScore,
	getIndividualRiskScore
} from './ranking';
import { FishboneBranch } from 'graph/dist/generated';

export const getItemAlteredByScenario = (item, scenario) => {
	if (!scenario?.id) return item?.o || item;

	let originalItem = item?.o || item;

	if (!originalItem) return null;

	// Si l'élément a des scénarios
	if (originalItem.scenarios && Array.isArray(originalItem.scenarios)) {
		const scenarioData = originalItem.scenarios.find(
			(s) => s.scenarioId === scenario.id
		);

		if (scenarioData) {
			return {
				...originalItem,
				x:
					scenarioData.x !== undefined
						? scenarioData.x
						: originalItem.x,
				y:
					scenarioData.y !== undefined
						? scenarioData.y
						: originalItem.y,
				trend:
					scenarioData.trend !== undefined
						? scenarioData.trend
						: originalItem.trend,
				likelihood:
					scenarioData.likelihood !== undefined
						? scenarioData.likelihood
						: originalItem.likelihood,
				impact:
					scenarioData.impact !== undefined
						? scenarioData.impact
						: originalItem.impact,
				completion:
					scenarioData.completion !== undefined
						? scenarioData.completion
						: originalItem.completion,
				weight:
					scenarioData.weight !== undefined
						? scenarioData.weight
						: originalItem.weight,
				enabled:
					scenarioData.enabled !== undefined
						? scenarioData.enabled
						: true,
				o: originalItem
			};
		}
	}

	// Si aucun scénario n'est trouvé, retourner l'élément original
	return originalItem;
};

export const vulnerabilityIndicators = function (indicators) {
	//console.log('-------- vulnerability', items);

	if (!indicators || indicators.length == 0) {
		return null;
	}

	// Filter out disabled indicators
	const enabledConstraints = indicators.filter((c) => c.enabled !== false);

	if (enabledConstraints.length == 0) {
		return null;
	}

	var total_weight = 0;
	var total = 0;

	for (var i = 0; i < enabledConstraints.length; i++) {
		if (enabledConstraints[i].weight !== 0)
			total_weight += enabledConstraints[i].weight || 1;

		if (enabledConstraints[i].weight !== 0)
			total +=
				colorToPercent(enabledConstraints[i].trend) *
				(enabledConstraints[i].weight || 1);
	}

	let currentPercentage = total_weight != 0 ? total / total_weight : 50;

	return currentPercentage;
};

export const vulnerabilityStakes = function (stakes) {
	//console.log('-------- vulnerability', stakes)

	if (!stakes || stakes.length == 0) {
		return null;
	}

	// Filter out disabled stakes
	const enabledStakes = stakes.filter((i) => i.enabled !== false);

	if (enabledStakes.length == 0) {
		return null;
	}

	var total_weight = 0;
	var total = 0;

	for (var i = 0; i < enabledStakes.length; i++) {
		if (enabledStakes[i].weight !== 0)
			total_weight += parseInt(enabledStakes[i].weight) || 1;

		if (enabledStakes[i].weight !== 0)
			total +=
				colorToPercent(enabledStakes[i].trend) *
				(enabledStakes[i].weight || 1);
	}

	let currentPercentage = total_weight != 0 ? total / total_weight : 50;

	return currentPercentage;
};

export const isItemInactive = (item) => {
	if (!item) return false;

	const distanceFromCenter = Math.sqrt(
		Math.pow(item.x, 2) + Math.pow(item.y, 2)
	);
	const isOutside = distanceFromCenter > 100;

	return isOutside;
};

export const isItemNeutral = (item) => {
	if (!item) return false;
	if (isItemInactive(item)) return false;

	if (item.y <= 5 && item.y >= -5) return true;

	return false;
};

export const isRiskOrOpportunityWorse = (risk) => {
	if (!risk) return false;
	if (!risk.o) return false;

	return risk.isOpportunity
		? getIndividualRiskScore(risk) < getIndividualRiskScore(risk.o)
		: getIndividualRiskScore(risk) > getIndividualRiskScore(risk.o);
};

export const isRiskOrOpportunityBetter = (risk) => {
	if (!risk) return false;
	if (!risk.o) return false;

	return risk.isOpportunity
		? getIndividualRiskScore(risk) > getIndividualRiskScore(risk.o)
		: getIndividualRiskScore(risk) < getIndividualRiskScore(risk.o);
};

export const isRiskOrOpportunityFieldBetter = (risk, field) => {
	if (!risk) return false;
	if (!risk.o) return false;

	return risk.isOpportunity
		? risk[field] > risk.o[field]
		: risk[field] < risk.o[field];
};

export const isRiskOrOpportunityFieldWorse = (risk, field) => {
	if (!risk) return false;
	if (!risk.o) return false;

	return risk.isOpportunity
		? risk[field] < risk.o[field]
		: risk[field] > risk.o[field];
};

export const isRiskOrOpportunityImpactBetter = (risk) => {
	return isRiskOrOpportunityFieldBetter(risk, 'impact');
};

export const isRiskOrOpportunityImpactWorse = (risk) => {
	return isRiskOrOpportunityFieldWorse(risk, 'impact');
};

export const isRiskOrOpportunityLikelihoodBetter = (risk) => {
	return isRiskOrOpportunityFieldBetter(risk, 'likelihood');
};

export const isRiskOrOpportunityLikelihoodWorse = (risk) => {
	return isRiskOrOpportunityFieldWorse(risk, 'likelihood');
};

export const isIndicatorOrConstraintWorse = (element) => {
	if (!element) return false;
	if (!element.o) return false;

	return (
		getStakeOrConstraintScore([element]) <
		getStakeOrConstraintScore([element.o])
	);
};

export const isIndicatorOrConstraintBetter = (element) => {
	if (!element) return false;
	if (!element.o) return false;

	return (
		getStakeOrConstraintScore([element]) >
		getStakeOrConstraintScore([element.o])
	);
};

export const getFishboneBranchCompletion = (fishboneBranch: FishboneBranch) => {
	const allPoints = fishboneBranch.items || [];

	let pointsWeight = 0;
	allPoints.forEach((p) => (pointsWeight += p.weight || 1));

	const completion =
		allPoints && allPoints.length > 0
			? sumBy(allPoints, (p) => (p.completion || 0) * (p.weight || 1)) /
				pointsWeight
			: 0;

	return completion;
};

export const getFishboneBranchTrend = (fishboneBranch: FishboneBranch) => {
	const allPoints = fishboneBranch.items || [];

	let pointsWeight = 0;
	allPoints.forEach((p) => (pointsWeight += p.weight || 1));

	const trend =
		allPoints && allPoints.length > 0
			? sumBy(
					allPoints,
					(p) =>
						(p.trend !== null ? parseInt(p.trend) : 50) *
						(p.weight || 1)
				) / pointsWeight
			: 0;

	return trend;
};

export const getFishboneBranchStartOn = (fishboneBranch: FishboneBranch) => {
	const allPoints = fishboneBranch.items || [];

	if (fishboneBranch.startOn) {
		return fishboneBranch.startOn;
	}

	let min = allPoints
		.map((p) => p.startOn)
		.filter((d) => d !== null && d !== undefined && d !== '')
		.reduce((a, b) => (a < b ? a : b), null);

	return min || null;
};

export const getFishboneBranchEndOn = (fishboneBranch: FishboneBranch) => {
	const allPoints = fishboneBranch.items || [];

	if (fishboneBranch.endOn) {
		return fishboneBranch.endOn;
	}

	let max = allPoints
		.map((p) => p.endOn)
		.filter((d) => d !== null && d !== undefined && d !== '')
		.reduce((a, b) => (a > b ? a : b), null);

	return max || null;
};
